import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { articles, getArticle } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import ArticleBody from "./components/ArticleBody";

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  const category = article ? getCategory(article.category) : undefined;

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter(
      (item) => item.category === article.category && item.slug !== article.slug,
    );
    const others = articles.filter(
      (item) => item.category !== article.category && item.slug !== article.slug,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const toc = useMemo(
    () =>
      article
        ? article.blocks
            .map((block, index) => ({ block, index }))
            .filter((item) => item.block.type === "heading")
            .map((item) => ({ id: `section-${item.index}`, text: item.block.text ?? "" }))
        : [],
    [article],
  );

  const index = article ? articles.findIndex((item) => item.slug === article.slug) : -1;
  const prev = index > 0 ? articles[index - 1] : undefined;
  const next = index >= 0 && index < articles.length - 1 ? articles[index + 1] : undefined;

  useSeo({
    title: article ? `${article.title}｜猎奇吃瓜` : "档案未找到｜猎奇吃瓜",
    description: article
      ? article.excerpt.slice(0, 150)
      : "该档案可能已被移动或不存在，返回悬疑档案库继续浏览。",
    keywords: article ? `${article.tags.join(",")},猎奇吃瓜,悬疑档案` : "猎奇吃瓜,悬疑档案",
    canonical: `/article/${slug}`,
    type: "article",
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-950">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-content flex-1 flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-900 text-3xl text-foreground-500">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-50">这份档案不存在</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-400">
            它可能已被移动、改名，或者从未被归档。回到档案库继续浏览其它悬案。
          </p>
          <Link
            to="/archives"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-arrow-left-line" /> 返回档案库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <div className="fixed left-0 top-0 z-[60] h-0.5 w-full bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      <SiteHeader />
      <main className="flex-1">
        <article className="mx-auto w-full max-w-content px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "悬疑档案", to: "/archives" },
              { label: category?.name ?? "档案", to: `/archives?category=${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6">
            <Link
              to={`/archives?category=${article.category}`}
              className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-3 py-1 text-xs text-accent-300"
            >
              <i className="ri-price-tag-3-line" /> {category?.name}
            </Link>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-50 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">{article.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-800 py-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" /> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" /> {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" /> {article.views.toLocaleString()} 次浏览
              </span>
            </div>
          </header>

          <div className="mt-7 overflow-hidden rounded-lg border border-background-800">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} - 猎奇吃瓜`}
              className="h-64 w-full object-cover object-top md:h-[420px]"
            />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <ArticleBody blocks={article.blocks} />

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/archives?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-700 px-3 py-1 text-xs text-foreground-400 transition-colors hover:border-primary-400 hover:text-primary-300"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <nav className="mt-10 grid grid-cols-1 gap-4 border-t border-background-800 pt-6 sm:grid-cols-2" aria-label="上下篇">
                {prev ? (
                  <Link
                    to={`/article/${prev.slug}`}
                    className="group rounded-lg border border-background-800 bg-background-900 p-4 transition-colors hover:border-primary-500/40"
                  >
                    <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                      <i className="ri-arrow-left-line" /> 上一篇
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-semibold text-foreground-100 transition-colors group-hover:text-primary-300">
                      {prev.title}
                    </p>
                  </Link>
                ) : (
                  <span />
                )}
                {next && (
                  <Link
                    to={`/article/${next.slug}`}
                    className="group rounded-lg border border-background-800 bg-background-900 p-4 transition-colors hover:border-primary-500/40 sm:text-right"
                  >
                    <span className="flex items-center gap-1.5 text-xs text-foreground-500 sm:justify-end">
                      下一篇 <i className="ri-arrow-right-line" />
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-semibold text-foreground-100 transition-colors group-hover:text-primary-300">
                      {next.title}
                    </p>
                  </Link>
                )}
              </nav>
            </div>

            <aside className="lg:sticky lg:top-28 lg:self-start">
              {toc.length > 0 && (
                <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                    <i className="ri-list-unordered text-primary-400" /> 内容目录
                  </h2>
                  <ol className="mt-4 space-y-2.5">
                    {toc.map((item, tocIndex) => (
                      <li key={item.id} className="flex gap-2 text-sm">
                        <span className="text-xs text-foreground-600">{String(tocIndex + 1).padStart(2, "0")}</span>
                        <a href={`#${item.id}`} className="text-foreground-400 transition-colors hover:text-primary-300">
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="mt-6 rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
                  <i className="ri-links-line text-primary-400" /> 相关档案
                </h2>
                <div className="mt-4 space-y-5">
                  {related.map((item) => (
                    <ArticleCard key={item.slug} article={item} variant="compact" />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />

      <JsonLd
        id="article-schema"
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          image: article.cover,
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: "zh-CN",
          articleSection: category?.name,
          keywords: article.tags.join(","),
          author: { "@type": "Person", name: article.author },
          publisher: { "@type": "Organization", name: "猎奇吃瓜" },
          mainEntityOfPage: `https://circleonly.cn/article/${article.slug}`,
        }}
      />
      <JsonLd
        id="article-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://circleonly.cn/" },
            { "@type": "ListItem", position: 2, name: "悬疑档案", item: "https://circleonly.cn/archives" },
            {
              "@type": "ListItem",
              position: 3,
              name: category?.name ?? "档案",
              item: `https://circleonly.cn/archives?category=${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 4,
              name: article.title,
              item: `https://circleonly.cn/article/${article.slug}`,
            },
          ],
        }}
      />
    </div>
  );
}